import { useEffect, useState } from "react";
import { Color, DEFAULT_POSITION, Square } from "chess.js";
import { GameState } from "../context/gameContextTypes";
import { v4 as uuidv4 } from "uuid";
import { MultiplayerState } from "../context/MultiplayerContext";
import { MULTIPLAYER } from "./constants";

/**
 * ===================
 * GENERAL GAME UTILS
 * ===================
 */

export const getApiUrl = () => {
    const hostname = window.location.hostname;

    // Check if running locally
    if (hostname === "localhost") {
        return "wss://api.int.chess.sbgtv.tech"; // fallback to int env
    }

    // For deployed environments
    return `wss://api.${hostname}`;
};

export function calcBoardWidth() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const minDimension = Math.min(width, height);

    // For larger screens, use a hybrid approach
    if (width > 1600) return Math.min(width * 0.35, minDimension * 0.95);
    if (width > 1200) return Math.min(width * 0.4, minDimension * 0.95);
    if (width > 1100) return Math.min(width * 0.4, minDimension * 0.95);
    if (width > 1000) return Math.min(width * 0.4, minDimension * 0.95);
    if (width > 900) return Math.min(width * 0.5, minDimension * 0.95);
    if (width > 800) return Math.min(width * 0.5, minDimension * 0.95);
    if (width > 700) return Math.min(width * 0.65, minDimension * 0.95);
    if (width > 600) return Math.min(width * 0.7, minDimension * 0.95);

    // Fallback for very small screens
    return minDimension * 0.7;
}

export function useResponsiveBoardWidth() {
    const [boardWidth, setBoardWidth] = useState(calcBoardWidth());

    useEffect(() => {
        const handleResize = () => setBoardWidth(calcBoardWidth());
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return boardWidth;
}

/**
 * Calculates the padding for the chessboard.
 * This provides a responsive padding for the chessboard border images.
 * These values were determined through calculations and trial & error. They should not be changed.
 * @returns the padding for the chessboard.
 */
export function calcBoardPadding() {
    const boardWidth = calcBoardWidth();
    return `${boardWidth * 0.095}px ${boardWidth * 0.098}px ${boardWidth * 0.11534}px ${boardWidth * 0.09724}px`;
}

/**
 * Extracts the individual move number from a FEN string.
 * FEN format: [board] [turn] [castling] [en passant] [halfmove] [fullmove]
 * Example: r3k2r/ppp2ppp/8/P2qpb2/1b1nn3/8/6PP/RNBQKBNR w KQkq - 0 11
 *
 * The fullmove counter increments after Black's move, so:
 * - Move 1 White = fullmove 1, turn 'w' → individual move 1
 * - Move 1 Black = fullmove 1, turn 'b' → individual move 2
 * - Move 2 White = fullmove 2, turn 'w' → individual move 3
 *
 * @param fenString the FEN string to parse.
 * @returns the individual move number (ply count).
 */
export function moveNumber(fenString: string | null): number {
    if (!fenString) fenString = DEFAULT_POSITION;

    const fenParts = fenString.split(" ");
    if (fenParts.length !== 6) {
        console.error("Invalid FEN string format:", fenString);
        return moveNumber(DEFAULT_POSITION); // Default to new game move number
    }

    const fullmoveCounter = parseInt(fenParts[5]) || 1;
    const activeColor = fenParts[1]; // 'w' for white, 'b' for black

    // Calculate individual move number (ply count)
    // Formula: (fullmove - 1) * 2 + (1 if white's turn, 2 if black's turn)
    return (fullmoveCounter - 1) * 2 + (activeColor === "w" ? 1 : 2);
}

/**
 * Finds the king's position from a FEN string.
 * Example format: r3k2r/ppp2ppp/8/P2qpb2/1b1nn3/8/6PP/RNBQKBNR w KQkq - 0 11
 *
 * @param {String} fenString the FEN string to parse.
 * @returns the king's square position in algebraic notation.
 */
export function findKingFromFenString(fenString: string, color: Color): Square | null {
    const boardPosition = fenString.split(" ")[0]; // Get the board position from the FEN string.
    const boardRows = boardPosition.split("/"); // Split the board position into rows.
    let kingPosition = null;

    // Search through the board rows to find the king's position.
    boardRows.forEach((row, rowIndex) => {
        row = row.replace(/\d/g, (digit) => ".".repeat(parseInt(digit))); // Remove numbers from the row.

        // Find the king's position in the row. Upper case for white, lower case for black in FEN.
        const colIndex = color === "w" ? row.indexOf("K") : row.indexOf("k");

        if (colIndex !== -1) {
            // Convert the row and column index to algebraic notation.
            kingPosition = "abcdefgh"[colIndex] + (8 - rowIndex);
        }
    });
    return kingPosition;
}

/**
 * Creates a random UUID for the user, and stores it in local storage.
 * @returns a random UUID for the player
 */
export function getUserId(): string {
    const userId = localStorage.getItem("userId");
    if (!userId) {
        const newID = uuidv4();
        localStorage.setItem("userId", newID);
        return newID;
    }
    return userId;
}

/**
 * Removes the existing lobbyId from the search
 * params. This is done to prevent the user from
 * attemping to join the game after they leave the lobby.
 */
export function removeLobbyIdFromSearchParams() {
    const url = new URL(window.location.href);
    url.searchParams.delete(MULTIPLAYER.LOBBY_ID_KEY);
    window.history.replaceState({}, "", url.toString());
}

/**
 * Gets the current opponent of the player for this game.
 *
 * @param multiplayerState the current multiplayer state
 * @returns the opponent that is being played
 */
export function getOpponent(multiplayerState: MultiplayerState) {
    return multiplayerState.playerColor === multiplayerState.p1.color
        ? multiplayerState.p2
        : multiplayerState.p1;
}

/**
 * Gets the current opponent plaeyr for this game.
 *
 * @param multiplayerState the current multiplayer state
 * @returns the player
 */
export function getMe(multiplayerState: MultiplayerState) {
    return multiplayerState.playerColor === multiplayerState.p1.color
        ? multiplayerState.p1
        : multiplayerState.p2;
}

export function getRightSquare(square: Square): Square | null {
    if (!square) return null;
    const col = square?.charCodeAt(0) - 97;
    const row = 8 - parseInt(square[1]);
    if (col === 7) return null;
    return (String.fromCharCode(col + 97 + 1) + (8 - row)) as Square;
}

export function getLeftSquare(square: Square): Square | null {
    if (!square) return null;
    const col = square?.charCodeAt(0) - 97;
    const row = 8 - parseInt(square[1]);
    if (col === 0) return null;
    return (String.fromCharCode(col + 97 - 1) + (8 - row)) as Square;
}

export function getUpSquare(square: Square): Square | null {
    if (!square) return null;
    const col = square?.charCodeAt(0) - 97;
    const row = 8 - parseInt(square[1]);
    if (row === 0) return null;
    return (String.fromCharCode(col + 97) + (8 - row + 1)) as Square;
}

export function getDownSquare(square: Square): Square | null {
    if (!square) return null;
    const col = square?.charCodeAt(0) - 97;
    const row = 8 - parseInt(square[1]);
    if (row === 7) return null;
    return (String.fromCharCode(col + 97) + (8 - row - 1)) as Square;
}

/** ================ */
/** SPECTATOR UTILS */
/** ================ */

export function getSpectatorBoardOrientiation(
    multiplayerState: MultiplayerState,
    gameState: GameState,
) {
    if (!gameState.game) {
        console.error("GameState.game is null when trying to get the spectator board orientation");
        return;
    }
    if (gameState.game.turn() === "w") {
        return "white";
    } else {
        return "black";
    }
}
